import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { FormAwareCustomSlide } from '@/modules/slideEditor/formAware/FormAwareCustomSlide';

import {
  SlideEditorBatchFormShape,
  usePresentationTemplateSlideEditorContext,
} from './PresentationTemplateSlideEditor.context';

export interface PresentationTemplateCustomSlideProps {
  bundleId: string;
}

/**
 * Template-editor binding for a custom-page bundle: resolves the batch-form
 * field paths from the bundle ID and delegates form handling to the shared
 * {@link FormAwareCustomSlide}.
 */
export function PresentationTemplateCustomSlide({
  bundleId,
}: PresentationTemplateCustomSlideProps) {
  const { control } = useFormContext<SlideEditorBatchFormShape>();
  const { getBundleIndexById } = usePresentationTemplateSlideEditorContext();

  // Look up the stable index for this bundle ID to access form fields
  const bundleIndex = useMemo(
    () => getBundleIndexById(bundleId),
    [getBundleIndexById, bundleId]
  );

  const customPage = useWatch({
    control,
    name: `bundles.${bundleIndex}.customPage`,
  });

  // Early return if customPage data isn't available - shouldn't happen for custom page bundles
  if (bundleIndex === -1 || !customPage) {
    return null;
  }

  return (
    <FormAwareCustomSlide<SlideEditorBatchFormShape>
      page={customPage.page}
      bundle={customPage.bundle}
      pageContentPath={`bundles.${bundleIndex}.customPage.page.pageContent`}
      pageNamePath={`bundles.${bundleIndex}.customPage.page.pageName`}
      customPromptPath={`bundles.${bundleIndex}.customPage.page.customPrompt`}
      highlightVariables
    />
  );
}
